import React, { useState, useEffect } from 'react';
import { Star, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { InlineTextEditor } from '@/components/ui/inline-text-editor';

interface Question {
  id: string;
  type: string;
  text: string;
  required: boolean;
  options?: { id: string; value: string }[];
  placeholder?: string;
}

interface UITextConfig {
  ratingTitle: string;
  ratingSubtitle: string;
  ratingCTA: string;
  emailLabel: string;
  emailPlaceholder: string;
  emailPlaceholderHigh: string; // New - high rating email placeholder
  emailPlaceholderLow: string; // New - low rating email placeholder
  consentText: string;
  consentTextLow: string; // New - low rating consent text
  continueButtonText: string;
  nextButtonText: string;
  formTitle: string;
  formSubtitle: string;
  formTitleHigh: string; // New - high rating form title
  formSubtitleHigh: string; // New - high rating form subtitle
  formTitleLow: string; // New - low rating form title
  formSubtitleLow: string; // New - low rating form subtitle
  nameLabel: string;
  namePlaceholder: string;
  submitButtonText: string;
  successTitle: string;
  successMessageHigh: string;
  successMessageLow: string;
}

const DEFAULT_UI_TEXT: UITextConfig = {
  ratingTitle: "Rate your experience",
  ratingSubtitle: "with {businessName}",
  ratingCTA: "Tap a star to continue",
  emailLabel: "Email Address",
  emailPlaceholder: "Enter your email for reward",
  emailPlaceholderHigh: "Enter your email for reward",
  emailPlaceholderLow: "Enter your email address",
  consentText: "I agree to terms",
  consentTextLow: "I agree to terms",
  continueButtonText: "Continue to Google Reviews",
  nextButtonText: "Next",
  formTitle: "Share Your Feedback",
  formSubtitle: "Help us improve by answering a few questions",
  formTitleHigh: "Share Your Experience",
  formSubtitleHigh: "We'd love to hear more about your excellent experience!",
  formTitleLow: "Share Your Feedback",
  formSubtitleLow: "Help us improve by answering a few questions",
  nameLabel: "Your Name (Optional)",
  namePlaceholder: "Enter your name",
  submitButtonText: "Submit Feedback",
  successTitle: "Thank You!",
  successMessageHigh: "Thank you for your excellent rating! We really appreciate your feedback.",
  successMessageLow: "Your feedback has been submitted successfully. Thank you for helping us improve!"
};

interface DirectPreviewFormProps {
  questions: Question[];
  starThreshold?: number;
  rewardEnabled?: boolean; // Legacy - keep for backwards compatibility
  enableStep2?: boolean; // Legacy - keep for backwards compatibility
  enableStep2High?: boolean; // New - enable step 2 for high ratings
  enableStep2Low?: boolean; // New - enable step 2 for low ratings
  highRatingEmailEnabled?: boolean; // High rating email collection
  highRatingEmailRequired?: boolean; // High rating email required
  highRatingRewardEnabled?: boolean; // High rating reward system
  lowRatingEmailEnabled?: boolean; // Low rating email collection
  lowRatingEmailRequired?: boolean; // Low rating email required
  lowRatingRewardEnabled?: boolean; // Low rating reward system
  businessName?: string;
  editingEnabled?: boolean;
  uiTextConfig?: UITextConfig;
  onUpdateUIText?: (field: keyof UITextConfig, value: string) => void;
  onUpdateQuestion?: (id: string, field: keyof Question, value: unknown) => void;
  initialStep?: number;
  onStepChange?: (step: number) => void;
  simulatedRating?: number; // New - simulated rating for preview mode
  step2TitleHigh?: string; // Custom title for high ratings (when email not enabled)
  step2SubtitleHigh?: string; // Custom subtitle for high ratings (when email not enabled)
  step2TitleLow?: string; // Custom title for low ratings (when email not enabled)
  step2SubtitleLow?: string; // Custom subtitle for low ratings (when email not enabled)
}

export const DirectPreviewForm: React.FC<DirectPreviewFormProps> = ({
  questions,
  starThreshold = 4,
  rewardEnabled = false,
  enableStep2 = true, // Legacy - fallback
  enableStep2High, // New - separate control for high ratings
  enableStep2Low, // New - separate control for low ratings
  highRatingEmailEnabled = false,
  highRatingEmailRequired = false,
  highRatingRewardEnabled = false,
  lowRatingEmailEnabled = false,
  lowRatingEmailRequired = false,
  lowRatingRewardEnabled = false,
  businessName = "Your Business",
  editingEnabled = false,
  uiTextConfig = DEFAULT_UI_TEXT,
  onUpdateUIText,
  onUpdateQuestion,
  initialStep = 1,
  onStepChange,
  simulatedRating,
  step2TitleHigh,
  step2SubtitleHigh,
  step2TitleLow,
  step2SubtitleLow
}) => {
  // Use separate high/low settings if provided, otherwise fall back to legacy enableStep2
  const effectiveEnableStep2High = enableStep2High !== undefined ? enableStep2High : enableStep2;
  const effectiveEnableStep2Low = enableStep2Low !== undefined ? enableStep2Low : enableStep2;
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [emailConsent, setEmailConsent] = useState(false);
  const [answers, setAnswers] = useState<{[key: string]: any}>({});
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [formData, setFormData] = useState<{[key: string]: any}>({});
  const [isEditingConsent, setIsEditingConsent] = useState(false);

  // Use simulatedRating if available, otherwise use rating state
  const effectiveRating = simulatedRating !== undefined ? simulatedRating : rating;

  // Sync step changes with parent
  useEffect(() => {
    setCurrentStep(initialStep);
  }, [initialStep]);


  // Handle internal step changes and notify parent
  const handleStepChange = (newStep: number) => {
    setCurrentStep(newStep);
    if (onStepChange) {
      onStepChange(newStep);
    }
  };

  // Handle back button
  const handleGoBack = () => {
    if (currentStep > 1) {
      // Reset rating and form data when going back
      setRating(0);
      setHoveredRating(0);
      setCustomerName('');
      setCustomerEmail('');
      setEmailConsent(false);
      setAnswers({});
      setFormData({});

      // Always go back to step 1 from any other step
      // since step 2 is the combined form step when rewards are enabled
      // and step 3 is only used when rewards are disabled
      handleStepChange(1);
    }
  };

  // Helper function to process UI text with business name placeholder
  const processUIText = (text: string): string => {
    return text.replace(/{businessName}/g, businessName);
  };

  // Handle UI text editing
  const handleUITextEdit = (field: keyof UITextConfig, value: string) => {
    if (editingEnabled && onUpdateUIText) {
      onUpdateUIText(field, value);
    }
  };

  // Handle question editing
  const handleQuestionEdit = (questionId: string, field: string, value: any) => {
    if (editingEnabled && onUpdateQuestion) {
      onUpdateQuestion(questionId, field as keyof Question, value);
    }
  };

  // EditablePlaceholderInput component for preview mode
  const EditablePlaceholderInput: React.FC<{
    id: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    placeholder: string;
    questionId: string;
    className?: string;
  }> = ({ id, value, onChange, placeholder, questionId, className = "" }) => {
    const [isEditingPlaceholder, setIsEditingPlaceholder] = useState(false);
    const [editPlaceholderValue, setEditPlaceholderValue] = useState(placeholder);
    
    useEffect(() => {
      if (!isEditingPlaceholder) {
        setEditPlaceholderValue(placeholder);
      }
    }, [placeholder, isEditingPlaceholder]);
    
    const handlePlaceholderSave = () => {
      if (editPlaceholderValue.trim() !== placeholder) {
        handleQuestionEdit(questionId, 'placeholder', editPlaceholderValue.trim());
      }
      setIsEditingPlaceholder(false);
    };

    const handlePlaceholderCancel = () => {
      setEditPlaceholderValue(placeholder);
      setIsEditingPlaceholder(false);
    };

    return (
      <div className="relative group">
        <Input
          id={id}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={className}
          disabled
        />
        {editingEnabled && (
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsEditingPlaceholder(true);
              setEditPlaceholderValue(placeholder);
            }}
            className="absolute top-2 right-2 w-4 h-4 bg-black/20 hover:bg-black/30 rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 z-20"
            title="Edit placeholder text"
          >
            <svg className="w-2 h-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
        )}
        
        {/* Placeholder editing modal/overlay */}
        {isEditingPlaceholder && editingEnabled && (
          <div 
            className="absolute top-0 left-0 right-0 bg-white border-2 border-blue-500 rounded-md p-2 z-30 shadow-lg"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="text"
              value={editPlaceholderValue}
              onChange={(e) => setEditPlaceholderValue(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handlePlaceholderSave();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  handlePlaceholderCancel();
                }
              }}
              onBlur={handlePlaceholderSave}
              className="w-full text-xs bg-transparent border-none outline-none"
              placeholder="Enter placeholder text"
              autoFocus
            />
            <div className="text-xs text-muted-foreground mt-1">
              Press Enter to save, Escape to cancel
            </div>
          </div>
        )}
      </div>
    );
  };

  // EditableTextarea component for preview mode  
  const EditablePlaceholderTextarea: React.FC<{
    id: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    placeholder: string;
    questionId: string;
    rows?: number;
    className?: string;
  }> = ({ id, value, onChange, placeholder, questionId, rows = 4, className = "" }) => {
    const [isEditingPlaceholder, setIsEditingPlaceholder] = useState(false);
    const [editPlaceholderValue, setEditPlaceholderValue] = useState(placeholder);
    
    useEffect(() => {
      if (!isEditingPlaceholder) {
        setEditPlaceholderValue(placeholder);
      }
    }, [placeholder, isEditingPlaceholder]);
    
    const handlePlaceholderSave = () => {
      if (editPlaceholderValue.trim() !== placeholder) {
        handleQuestionEdit(questionId, 'placeholder', editPlaceholderValue.trim());
      }
      setIsEditingPlaceholder(false);
    };

    const handlePlaceholderCancel = () => {
      setEditPlaceholderValue(placeholder);
      setIsEditingPlaceholder(false);
    };

    return (
      <div className="relative group">
        <Textarea
          id={id}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          rows={rows}
          className={className}
          disabled
        />
        {editingEnabled && (
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsEditingPlaceholder(true);
              setEditPlaceholderValue(placeholder);
            }}
            className="absolute top-2 right-2 w-4 h-4 bg-black/20 hover:bg-black/30 rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 z-20"
            title="Edit placeholder text"
          >
            <svg className="w-2 h-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
        )}
        
        {/* Placeholder editing modal/overlay */}
        {isEditingPlaceholder && editingEnabled && (
          <div 
            className="absolute top-0 left-0 right-0 bg-white border-2 border-blue-500 rounded-md p-2 z-30 shadow-lg"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="text"
              value={editPlaceholderValue}
              onChange={(e) => setEditPlaceholderValue(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handlePlaceholderSave();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  handlePlaceholderCancel();
                }
              }}
              onBlur={handlePlaceholderSave}
              className="w-full text-xs bg-transparent border-none outline-none"
              placeholder="Enter placeholder text"
              autoFocus
            />
            <div className="text-xs text-muted-foreground mt-1">
              Press Enter to save, Escape to cancel
            </div>
          </div>
        )}
      </div>
    );
  };

  // EditableUIPlaceholderInput component for UI text placeholders
  const EditableUIPlaceholderInput: React.FC<{
    id: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    placeholderKey: keyof UITextConfig;
    className?: string;
    type?: string;
  }> = ({ id, value, onChange, placeholderKey, className = "", type = "text" }) => {
    const [isEditingPlaceholder, setIsEditingPlaceholder] = useState(false);
    const [editPlaceholderValue, setEditPlaceholderValue] = useState(uiTextConfig[placeholderKey]);
    
    useEffect(() => {
      if (!isEditingPlaceholder) {
        setEditPlaceholderValue(uiTextConfig[placeholderKey]);
      }
    }, [uiTextConfig[placeholderKey], isEditingPlaceholder, placeholderKey]);
    
    const handlePlaceholderSave = () => {
      if (editPlaceholderValue.trim() !== uiTextConfig[placeholderKey]) {
        handleUITextEdit(placeholderKey, editPlaceholderValue.trim());
      }
      setIsEditingPlaceholder(false);
    };

    const handlePlaceholderCancel = () => {
      setEditPlaceholderValue(uiTextConfig[placeholderKey]);
      setIsEditingPlaceholder(false);
    };

    return (
      <div className="relative group">
        <Input
          id={id}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={uiTextConfig[placeholderKey]}
          className={className}
          disabled
        />
        {editingEnabled && (
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsEditingPlaceholder(true);
              setEditPlaceholderValue(uiTextConfig[placeholderKey]);
            }}
            className="absolute top-2 right-2 w-4 h-4 bg-black/20 hover:bg-black/30 rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 z-20"
            title="Edit placeholder text"
          >
            <svg className="w-2 h-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
        )}
        
        {/* Placeholder editing modal/overlay */}
        {isEditingPlaceholder && editingEnabled && (
          <div 
            className="absolute top-0 left-0 right-0 bg-white border-2 border-blue-500 rounded-md p-2 z-30 shadow-lg"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="text"
              value={editPlaceholderValue}
              onChange={(e) => setEditPlaceholderValue(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handlePlaceholderSave();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  handlePlaceholderCancel();
                }
              }}
              onBlur={handlePlaceholderSave}
              className="w-full text-xs bg-transparent border-none outline-none"
              placeholder="Enter placeholder text"
              autoFocus
            />
            <div className="text-xs text-muted-foreground mt-1">
              Press Enter to save, Escape to cancel
            </div>
          </div>
        )}
      </div>
    );
  };

  // EditableButton component for preview mode
  const EditableButton: React.FC<{
    onClick: () => void;
    className: string;
    textKey: keyof UITextConfig;
    children?: React.ReactNode;
  }> = ({ onClick, className, textKey, children }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(uiTextConfig[textKey]);
    
    useEffect(() => {
      if (!isEditing) {
        setEditValue(uiTextConfig[textKey]);
      }
    }, [uiTextConfig[textKey], isEditing, textKey]);
    
    const handleSave = () => {
      if (editValue.trim() !== uiTextConfig[textKey]) {
        handleUITextEdit(textKey, editValue.trim());
      }
      setIsEditing(false);
    };

    const handleCancel = () => {
      setEditValue(uiTextConfig[textKey]);
      setIsEditing(false);
    };

    if (isEditing && editingEnabled) {
      return (
        <div className="relative w-full">
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSave();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                handleCancel();
              }
            }}
            onBlur={handleSave}
            className="w-full bg-white/90 text-gray-900 py-2.5 px-3 rounded-lg text-sm font-medium border-2 border-blue-500 focus:outline-none"
            autoFocus
          />
        </div>
      );
    }

    return (
      <div className="relative group">
        <button onClick={onClick} className={className}>
          {uiTextConfig[textKey]}
          {children}
        </button>
        {editingEnabled && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
              setEditValue(uiTextConfig[textKey]);
            }}
            className="absolute top-1 right-1 w-5 h-5 bg-black/20 hover:bg-black/30 rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
            title="Edit button text"
          >
            <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
        )}
      </div>
    );
  };

  const handleRatingClick = (selectedRating: number) => {
    // Don't allow rating clicks in preview mode with simulated rating
    if (simulatedRating !== undefined) {
      return;
    }

    setRating(selectedRating);
    const threshold = starThreshold || 4;
    const isHighRating = selectedRating >= threshold;

    // Check if step 2 is enabled for this rating level
    const step2Enabled = isHighRating ? effectiveEnableStep2High : effectiveEnableStep2Low;

    if (!step2Enabled) {
      // If step 2 is disabled for this rating level, go directly to success page
      handleStepChange(4);
      return;
    }

    if (isHighRating) {
      // High rating flow
      if (highRatingEmailEnabled) {
        handleStepChange(2); // Go to email/form step for high ratings
      } else {
        handleStepChange(3); // Go directly to form step (or success if no questions)
      }
    } else {
      // Low rating flow
      if (lowRatingEmailEnabled) {
        handleStepChange(2); // Go to email/form step for low ratings
      } else {
        handleStepChange(3); // Go directly to form step
      }
    }
  };

  const handleEmailStep = () => {
    // This function is no longer used as email and form are combined in step 2
    // when rewards are enabled. Keep for backward compatibility.
    if (rating >= (starThreshold || 4)) {
      handleStepChange(4); // Success (would have redirected to Google)
    } else {
      handleStepChange(3); // Go to custom form
    }
  };

  const handleAnswerChange = (questionId: string, value: any) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    setFormData(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check email requirements based on rating and settings
    const isHighRating = effectiveRating >= (starThreshold || 4);
    const emailRequired = isHighRating ? highRatingEmailRequired : lowRatingEmailRequired;
    const emailEnabled = isHighRating ? highRatingEmailEnabled : lowRatingEmailEnabled;
    
    // If email is enabled and required, validate email field
    if (emailEnabled && emailRequired && !customerEmail.trim()) {
      // In a real form, you'd show an error message here
      // For preview, we'll just prevent submission silently
      return;
    }
    
    // Validate required questions
    for (const question of questions) {
      if (question.required && (!answers[question.id] || answers[question.id] === '')) {
        // In a real form, you'd show an error message here  
        // For preview, we'll just prevent submission silently
        return;
      }
    }
    
    handleStepChange(4);
  };

  const renderQuestion = (question: Question) => {
    switch (question.type) {
      case 'text':
        return (
          <div key={question.id} className="space-y-1">
            <Label 
              htmlFor={editingEnabled ? undefined : question.id}
              onClick={editingEnabled ? (e) => e.preventDefault() : undefined}
            >
              {editingEnabled ? (
                <InlineTextEditor
                  value={question.text}
                  onSave={(newText) => handleQuestionEdit(question.id, 'text', newText)}
                  placeholder="Question text"
                  className="text-sm font-medium"
                />
              ) : (
                question.text
              )}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <EditablePlaceholderInput
              id={question.id}
              value={answers[question.id] || ''}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              placeholder={question.placeholder || "Your answer..."}
              questionId={question.id}
            />
          </div>
        );

      case 'textarea':
        return (
          <div key={question.id} className="space-y-1">
            <Label 
              htmlFor={editingEnabled ? undefined : question.id}
              onClick={editingEnabled ? (e) => e.preventDefault() : undefined}
            >
              {editingEnabled ? (
                <InlineTextEditor
                  value={question.text}
                  onSave={(newText) => handleQuestionEdit(question.id, 'text', newText)}
                  placeholder="Question text"
                  className="text-sm font-medium"
                />
              ) : (
                question.text
              )}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <EditablePlaceholderTextarea
              id={question.id}
              value={answers[question.id] || ''}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              placeholder={question.placeholder || "Your detailed response..."}
              questionId={question.id}
              rows={4}
            />
          </div>
        );

      case 'multiple_choice':
        return (
          <div key={question.id} className="space-y-1">
            <Label>
              {editingEnabled ? (
                <InlineTextEditor
                  value={question.text}
                  onSave={(newText) => handleQuestionEdit(question.id, 'text', newText)}
                  placeholder="Question text"
                  className="text-sm font-medium"
                />
              ) : (
                question.text
              )}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <RadioGroup
              value={answers[question.id] || ''}
              onValueChange={(value) => handleAnswerChange(question.id, value)}
              disabled
            >
              {(question.options || []).map((option) => (
                <div key={option.id} className="flex items-center space-x-2">
                  <RadioGroupItem value={option.value} id={`${question.id}-${option.id}`} disabled />
                  <Label htmlFor={`${question.id}-${option.id}`} className="flex-1 min-h-[1.5rem] flex items-center">
                    {editingEnabled ? (
                      <InlineTextEditor
                        value={option.value || "Option text"}
                        onSave={(newValue) => {
                          const updatedOptions = (question.options || []).map(opt =>
                            opt.id === option.id ? { ...opt, value: newValue } : opt
                          );
                          handleQuestionEdit(question.id, 'options', updatedOptions);
                        }}
                        placeholder="Option text"
                        className="text-sm min-w-[120px] w-full"
                      />
                    ) : (
                      option.value || "Option text"
                    )}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        );

      case 'checkboxes':
        return (
          <div key={question.id} className="space-y-1">
            <Label>
              {editingEnabled ? (
                <InlineTextEditor
                  value={question.text}
                  onSave={(newText) => handleQuestionEdit(question.id, 'text', newText)}
                  placeholder="Question text"
                  className="text-sm font-medium"
                />
              ) : (
                question.text
              )}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="space-y-2">
              {(question.options || []).map((option) => (
                <div key={option.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`${question.id}-${option.id}`}
                    checked={(answers[question.id] || []).includes(option.value)}
                    onCheckedChange={(checked) => {
                      const currentAnswers = answers[question.id] || [];
                      if (checked) {
                        handleAnswerChange(question.id, [...currentAnswers, option.value]);
                      } else {
                        handleAnswerChange(question.id, currentAnswers.filter((v: string) => v !== option.value));
                      }
                    }}
                    disabled
                  />
                  <Label htmlFor={`${question.id}-${option.id}`} className="flex-1 min-h-[1.5rem] flex items-center">
                    {editingEnabled ? (
                      <InlineTextEditor
                        value={option.value || "Option text"}
                        onSave={(newValue) => {
                          const updatedOptions = (question.options || []).map(opt =>
                            opt.id === option.id ? { ...opt, value: newValue } : opt
                          );
                          handleQuestionEdit(question.id, 'options', updatedOptions);
                        }}
                        placeholder="Option text"
                        className="text-sm min-w-[120px] w-full"
                      />
                    ) : (
                      option.value || "Option text"
                    )}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        );

      case 'rating':
        return (
          <div key={question.id} className="space-y-1">
            <Label>
              {editingEnabled ? (
                <InlineTextEditor
                  value={question.text}
                  onSave={(newText) => handleQuestionEdit(question.id, 'text', newText)}
                  placeholder="Question text"
                  className="text-sm font-medium"
                />
              ) : (
                question.text
              )}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="flex justify-start space-x-1 py-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  size={24}
                  className={`cursor-pointer transition-all duration-200 ${
                    ((answers[question.id] as number) >= star)
                      ? "text-yellow-400 fill-yellow-400"
                      : "text-gray-300 hover:text-yellow-200"
                  }`}
                  onClick={() => handleAnswerChange(question.id, star)}
                />
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-background min-h-full">
      <div className="px-3 py-3">
        <div className="min-h-[450px] flex flex-col justify-center">
          {/* Step 1: Rating */}
          {currentStep === 1 && (
            <div className="text-center space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2">
                    {editingEnabled ? (
                      <InlineTextEditor
                        value={uiTextConfig.ratingTitle}
                        onSave={(newText) => handleUITextEdit('ratingTitle', newText)}
                        placeholder="Rate your experience"
                        className="text-lg font-semibold"
                      />
                    ) : (
                      uiTextConfig.ratingTitle
                    )}
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    {editingEnabled ? (
                      <InlineTextEditor
                        value={uiTextConfig.ratingSubtitle}
                        displayValue={processUIText(uiTextConfig.ratingSubtitle)}
                        onSave={(newText) => handleUITextEdit('ratingSubtitle', newText)}
                        placeholder="How would you rate your overall experience?"
                        className="text-muted-foreground"
                      />
                    ) : (
                      processUIText(uiTextConfig.ratingSubtitle)
                    )}
                  </p>
                </div>
                
                <div className="flex justify-center space-x-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      size={36}
                      className={`cursor-pointer transition-all duration-200 ${
                        hoveredRating >= star
                          ? "text-yellow-400 fill-yellow-400 scale-110"
                          : "text-gray-300 hover:text-yellow-200"
                      }`}
                      onClick={() => handleRatingClick(star)}
                      onMouseEnter={() => setHoveredRating(star)}
                      onMouseLeave={() => setHoveredRating(0)}
                    />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground">
                  {editingEnabled ? (
                    <InlineTextEditor
                      value={uiTextConfig.ratingCTA}
                      onSave={(newText) => handleUITextEdit('ratingCTA', newText)}
                      placeholder="Click a star to continue"
                      className="text-sm text-muted-foreground"
                    />
                  ) : (
                    uiTextConfig.ratingCTA
                  )}
                </p>
              </div>
            )}

          {/* Step 2: Email Collection + Form (combined when rewards enabled) */}
          {currentStep === 2 && (
            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div className="text-center">
                <h3 className="text-base font-semibold mb-1">
                    {editingEnabled ? (
                      <InlineTextEditor
                        value={effectiveRating >= (starThreshold || 4)
                          ? uiTextConfig.formTitleHigh
                          : uiTextConfig.formTitleLow
                        }
                        onSave={(newText) => handleUITextEdit(
                          effectiveRating >= (starThreshold || 4) ? 'formTitleHigh' : 'formTitleLow',
                          newText
                        )}
                        placeholder={effectiveRating >= (starThreshold || 4) ? "Share Your Experience" : "Share Your Feedback"}
                        className="text-base font-semibold"
                      />
                    ) : (
                      effectiveRating >= (starThreshold || 4)
                        ? uiTextConfig.formTitleHigh
                        : uiTextConfig.formTitleLow
                    )}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {editingEnabled ? (
                      <InlineTextEditor
                        value={effectiveRating >= (starThreshold || 4)
                          ? uiTextConfig.formSubtitleHigh
                          : uiTextConfig.formSubtitleLow
                        }
                        onSave={(newText) => handleUITextEdit(
                          effectiveRating >= (starThreshold || 4) ? 'formSubtitleHigh' : 'formSubtitleLow',
                          newText
                        )}
                        placeholder={effectiveRating >= (starThreshold || 4)
                          ? "We'd love to hear more about your excellent experience!"
                          : "Help us improve by answering a few questions"
                        }
                        className="text-muted-foreground"
                      />
                    ) : (
                      effectiveRating >= (starThreshold || 4)
                        ? uiTextConfig.formSubtitleHigh
                        : uiTextConfig.formSubtitleLow
                    )}
                  </p>
                </div>
                
                {/* Email Collection Section (based on rating and email settings) */}
                {((effectiveRating >= (starThreshold || 4) && highRatingEmailEnabled) || (effectiveRating < (starThreshold || 4) && lowRatingEmailEnabled)) && (
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="customer-email">
                        {editingEnabled ? (
                          <InlineTextEditor
                            value={uiTextConfig.emailLabel}
                            onSave={(newText) => handleUITextEdit('emailLabel', newText)}
                            placeholder="Email Address"
                            className="text-sm font-medium"
                          />
                        ) : (
                          uiTextConfig.emailLabel
                        )}
                      </Label>
                      <EditableUIPlaceholderInput
                        id="customer-email"
                        type="email"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        placeholderKey={effectiveRating >= (starThreshold || 4) ? "emailPlaceholderHigh" : "emailPlaceholderLow"}
                        className="mt-1"
                      />
                    </div>
                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="email-consent"
                        className="mt-0.5"
                        checked={emailConsent}
                        onCheckedChange={(checked) => setEmailConsent(checked as boolean)}
                        disabled
                      />
                      <Label htmlFor="email-consent" className="text-sm text-muted-foreground cursor-pointer relative group">
                        {isEditingConsent && editingEnabled ? (
                          <input
                            type="text"
                            value={effectiveRating >= (starThreshold || 4) ? uiTextConfig.consentText : uiTextConfig.consentTextLow}
                            onChange={(e) => {
                              const field = effectiveRating >= (starThreshold || 4) ? 'consentText' : 'consentTextLow';
                              handleUITextEdit(field, e.target.value);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                setIsEditingConsent(false);
                              } else if (e.key === 'Escape') {
                                setIsEditingConsent(false);
                              }
                            }}
                            onBlur={() => setIsEditingConsent(false)}
                            autoFocus
                            className="w-full bg-white/20 border border-white/30 rounded px-2 py-1 text-sm text-muted-foreground focus:bg-white/30 focus:border-white/50 focus:outline-none focus:ring-2 focus:ring-white/20"
                          />
                        ) : (
                          <>
                            {(() => {
                              const consentText = effectiveRating >= (starThreshold || 4)
                                ? uiTextConfig.consentText
                                : uiTextConfig.consentTextLow;

                              // Split the text at the last word to add privacy link
                              const words = consentText.trim().split(/\s+/);
                              const lastWord = words[words.length - 1];
                              const beforeLastWord = words.slice(0, -1).join(' ');

                              return (
                                <>
                                  {beforeLastWord}{' '}
                                  <span className="text-primary underline">
                                    {lastWord}
                                  </span>
                                  {' *'}
                                </>
                              );
                            })()}
                            {editingEnabled && (
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setIsEditingConsent(true);
                                }}
                                className="ml-2 inline-flex w-4 h-4 bg-black/20 hover:bg-black/30 rounded-full items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                                title="Edit consent text"
                              >
                                <svg className="w-2 h-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                              </button>
                            )}
                          </>
                        )}
                      </Label>
                    </div>
                  </div>
                )}

                {/* Form Fields */}
                <div className="space-y-3">
                  {/* Custom Questions - Filter by visibility based on rating */}
                  {questions
                    .filter(q => {
                      const isHighRating = effectiveRating >= (starThreshold || 4);
                      if (isHighRating) {
                        return q.visible_on_high_rating !== false;
                      } else {
                        return q.visible_on_low_rating !== false;
                      }
                    })
                    .map(renderQuestion)}
                </div>

                {/* Single Submit Button */}
                <div className="pt-3">
                  <EditableButton
                    onClick={() => {}}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-6 rounded-md text-sm font-medium"
                    textKey="submitButtonText"
                  />
                </div>
              </form>
            )}

          {/* Step 3: Custom Form */}
          {currentStep === 3 && (
            <form onSubmit={handleFormSubmit} className="space-y-3">
              <div className="text-center mb-3">
                <h3 className="text-base font-semibold mb-1">
                    {editingEnabled ? (
                      <InlineTextEditor
                        value={effectiveRating >= (starThreshold || 4)
                          ? uiTextConfig.formTitleHigh
                          : uiTextConfig.formTitleLow
                        }
                        onSave={(newText) => handleUITextEdit(
                          effectiveRating >= (starThreshold || 4) ? 'formTitleHigh' : 'formTitleLow',
                          newText
                        )}
                        placeholder={effectiveRating >= (starThreshold || 4) ? "Share Your Experience" : "Share Your Feedback"}
                        className="text-base font-semibold"
                      />
                    ) : (
                      effectiveRating >= (starThreshold || 4)
                        ? uiTextConfig.formTitleHigh
                        : uiTextConfig.formTitleLow
                    )}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {editingEnabled ? (
                      <InlineTextEditor
                        value={effectiveRating >= (starThreshold || 4)
                          ? uiTextConfig.formSubtitleHigh
                          : uiTextConfig.formSubtitleLow
                        }
                        onSave={(newText) => handleUITextEdit(
                          effectiveRating >= (starThreshold || 4) ? 'formSubtitleHigh' : 'formSubtitleLow',
                          newText
                        )}
                        placeholder={effectiveRating >= (starThreshold || 4)
                          ? "We'd love to hear more about your excellent experience!"
                          : "Help us improve by answering a few questions"
                        }
                        className="text-muted-foreground"
                      />
                    ) : (
                      effectiveRating >= (starThreshold || 4)
                        ? uiTextConfig.formSubtitleHigh
                        : uiTextConfig.formSubtitleLow
                    )}
                  </p>
                </div>

                {/* Custom Questions - Filter by visibility based on rating */}
                {questions
                  .filter(q => {
                    const isHighRating = effectiveRating >= (starThreshold || 4);
                    if (isHighRating) {
                      return q.visible_on_high_rating !== false;
                    } else {
                      return q.visible_on_low_rating !== false;
                    }
                  })
                  .map(renderQuestion)}

                {/* Submit Button */}
                <div className="pt-3">
                  <EditableButton
                    onClick={() => {}}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-6 rounded-md text-sm font-medium disabled:pointer-events-none disabled:opacity-50"
                    textKey="submitButtonText"
                  />
                </div>
              </form>
            )}

          {/* Step 4: Success */}
          {currentStep === 4 && (
            <div className="text-center space-y-3">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3 mx-auto">
                <div className="text-xl">✅</div>
              </div>
              <h3 className="text-base font-semibold mb-1">
                  {editingEnabled ? (
                    <InlineTextEditor
                      value={uiTextConfig.successTitle}
                      onSave={(newText) => handleUITextEdit('successTitle', newText)}
                      placeholder="Thank You!"
                      className="text-lg font-semibold"
                    />
                  ) : (
                    uiTextConfig.successTitle
                  )}
                </h3>
                <p className="text-muted-foreground text-xs">
                  {editingEnabled ? (
                    <InlineTextEditor
                      value={rating >= starThreshold ? uiTextConfig.successMessageHigh : uiTextConfig.successMessageLow}
                      onSave={(newText) => handleUITextEdit(rating >= starThreshold ? 'successMessageHigh' : 'successMessageLow', newText)}
                      placeholder={rating >= starThreshold ? 'Your feedback has been submitted and you were redirected to Google Reviews.' : 'Your feedback has been submitted successfully. We appreciate your input!'}
                      className="text-muted-foreground"
                    />
                  ) : (
                    rating >= starThreshold 
                      ? uiTextConfig.successMessageHigh
                      : uiTextConfig.successMessageLow
                  )}
                </p>
              </div>
            )}
        </div>
        
        {/* Back Button - Bottom Right - Hide on success step (step 4) */}
        {currentStep > 1 && currentStep < 4 && (
          <button
            onClick={handleGoBack}
            className="absolute bottom-4 right-4 w-10 h-10 bg-black/20 hover:bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center transition-all z-20"
            title="Go back"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
        )}
      </div>
    </div>
  );
};

export default DirectPreviewForm;